import { useState } from 'react';
import { Check, Search, UserPlus, X } from 'lucide-react';
import { useAuth } from '@/app/providers';
import { Avatar, Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { stripHandle } from '@/features/profile/helpers';
import { useFriends, useProfileSearch, useSendFriendRequestTo } from '@/features/profile/hooks/useSocial';

/**
 * Add-friends control for the inbox header: a compact icon that expands into a
 * search field for finding other members by username and sending a friend
 * request inline.
 */
export function AddFriendSearch() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sent, setSent] = useState<Set<string>>(new Set());
  const { data: rawResults = [], isFetching } = useProfileSearch(q, user?.id);
  const { data: friends = [] } = useFriends(user?.id);
  const friendIds = new Set(friends.map((f) => f.id));
  // Don't surface people you're already friends with — only new connections.
  const results = rawResults.filter((p) => !friendIds.has(p.id));
  const sendReq = useSendFriendRequestTo(user?.id);

  function close() {
    setOpen(false);
    setQ('');
  }

  function add(id: string) {
    sendReq.mutate(id, {
      onSuccess: () => {
        setSent((prev) => new Set(prev).add(id));
        toast('Friend request sent.');
      },
      onError: (err) => toast((err as Error).message || 'Could not send request', 'error'),
    });
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Add friends"
        aria-label="Add friends"
        className="grid h-10 w-10 flex-none place-items-center rounded-full bg-white/[0.045] text-accent transition-colors hover:bg-white/[0.09] hover:text-white"
      >
        <UserPlus size={18} />
      </button>
    );
  }

  const showResults = q.trim().length >= 2;

  return (
    <div className="relative">
      <div className="flex h-10 w-[260px] items-center gap-2.5 rounded-pill border border-white/15 bg-white/[0.05] px-4">
        <Search size={15} className="flex-none text-white/45" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find people by username"
          autoComplete="off"
          className="min-w-0 flex-1 bg-transparent text-[13px] font-semibold text-white outline-none placeholder:text-white/40"
          onKeyDown={(e) => {
            if (e.key === 'Escape') close();
          }}
        />
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="flex h-6 w-6 flex-none items-center justify-center text-white/45 transition-colors hover:text-white"
        >
          <X size={14} />
        </button>
      </div>

      {showResults && (
        <div className="chat-scroll absolute right-0 top-[calc(100%+8px)] z-30 max-h-[360px] w-[300px] overflow-y-auto rounded-xl border border-white/10 bg-[rgba(16,16,22,0.98)] p-1.5 shadow-[0_24px_60px_rgba(0,0,0,0.6)] backdrop-blur-2xl">
          {isFetching && !results.length ? (
            <div className="grid place-items-center py-6">
              <Spinner className="h-4 w-4" />
            </div>
          ) : results.length ? (
            results.map((p) => {
              const name = p.display_name || stripHandle(p.handle) || 'WINR member';
              const wasSent = sent.has(p.id);
              return (
                <div key={p.id} className="flex items-center gap-3 rounded-lg px-2.5 py-2">
                  <Avatar src={p.avatar_url} name={name} size={34} className="border border-white/10" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-bold text-white">{name}</div>
                    {p.handle && (
                      <div className="truncate text-[12px] font-semibold text-white/45">@{stripHandle(p.handle)}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={wasSent || sendReq.isPending}
                    onClick={() => add(p.id)}
                    className="flex h-8 flex-none items-center gap-1.5 rounded-full bg-accent px-3 text-[12px] font-black text-black transition-all hover:brightness-110 disabled:cursor-default disabled:bg-white/[0.08] disabled:text-white/55"
                  >
                    {wasSent ? <Check size={14} /> : <UserPlus size={14} />}
                    {wasSent ? 'Sent' : 'Add'}
                  </button>
                </div>
              );
            })
          ) : (
            <div className="px-3 py-6 text-center text-[12px] font-semibold text-white/45">
              No members match “{q.trim()}”.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
